import React, { useEffect, useState } from 'react';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { QueueMessagePreview, QueueState } from '../../api/queueMonitor';
import { getSyncStatus, SyncStatus } from '../../api/requeue';
import { apiError, track } from '../data/usePoll';
import { ago, number } from '../data/format';
import { CheckRow, Empty, EnvChip, Icon, Loading } from '../components/ui';
import { EnvKind } from '../data/cockpit';
import { objectName } from './issueModel';

const DIRECTION: Record<string, string> = { ChargebeeToExact: 'Heen · naar Exact', ExactToChargebee: 'Terug · naar Chargebee' };

/** Where one poisoned object stands now: maybe it reached Exact another way since. */
const Status = ({ customer, environment, message, queue }: { customer: string; environment: string; message: QueueMessagePreview; queue: QueueState }) => {
    const axios = useAxiosSwagger();
    const [status, setStatus] = useState<SyncStatus>();
    const [error, setError] = useState<string>();

    useEffect(() => {
        if (!axios || !message.objectType || !message.relationId) return;
        track(getSyncStatus(axios, customer, environment, message.objectType, message.relationId, queue.poisonOf ?? queue.name))
            .then(setStatus)
            .catch((e) => setError(apiError(e)));
    }, [axios, customer, environment, message, queue]);

    if (!message.objectType || !message.relationId) return <Empty>Dit bericht noemt geen object.</Empty>;
    if (error) return <Empty>{error}</Empty>;
    if (!status) return <Loading>Stand van dit object ophalen…</Loading>;

    return (
        <div className="cb checks">
            <CheckRow tone={status.syncedToExact ? 'ok' : 'bad'} label={status.syncedToExact ? 'Staat in Exact' : 'Niet in Exact'} value={status.dependingId ? status.dependingId.slice(0, 8) : undefined} />
            <CheckRow tone={status.failCount > 0 ? 'warn' : 'ok'} label="Mislukte pogingen" value={number(status.failCount)} />
            {status.lastState && <CheckRow tone="todo" label="Laatste stand" value={status.lastState} />}
            {status.lastMessage && (
                <details>
                    <summary className="mut" style={{ cursor: 'pointer', fontSize: 13, padding: '8px 0' }}>Melding van de connector</summary>
                    <div className="codebox">{status.lastMessage}</div>
                </details>
            )}
        </div>
    );
};

/** The poison queues of one environment: what gave up, since when, and where each object stands now. */
export const PoisonDrawer = ({
    customer,
    environment,
    kind,
    queues,
    onClose,
}: {
    customer: string;
    environment: string;
    kind: EnvKind;
    queues: QueueState[];
    onClose: () => void;
}) => {
    const poison = queues.filter((q) => q.stage === 'Poison');
    const [pick, setPick] = useState(() => (poison.find((q) => q.depth > 0) ?? poison[0])?.name);
    const [open, setOpen] = useState<string>();
    const queue = poison.find((q) => q.name === pick);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <>
            <button className="scrim" aria-label="Sluiten" onClick={onClose} />
            <aside className="drawer" aria-label="Poison">
                <div className="dhead">
                    <div className="hrow" style={{ justifyContent: 'space-between' }}>
                        <div>
                            <div className="eyebrow">{customer} · {environment}</div>
                            <div className="h1" style={{ fontSize: 22 }}>
                                Poison <EnvChip kind={kind} environment={environment} />
                            </div>
                        </div>
                        <button className="btn sm ghost" onClick={onClose}>
                            <Icon name="back" size={14} />
                            Esc
                        </button>
                    </div>
                    {poison.length > 1 && (
                        <div className="seg wrap" role="group" aria-label="Queue">
                            {poison.map((q) => (
                                <button key={q.name} className={q.name === pick ? 'on' : ''} onClick={() => setPick(q.name)}>
                                    <span className="mono">{q.name}</span>
                                    <span className={`cnt ${q.depth > 0 ? 'red' : ''}`}>{q.depth < 0 ? '?' : q.depth}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {poison.length === 0 && <Empty>Geen poison-queues gevonden voor deze omgeving.</Empty>}

                {queue && (
                    <>
                        <div className="dsec">
                            <dl className="kv">
                                <dt>Richting</dt>
                                <dd>{DIRECTION[queue.pipeline] ?? queue.pipeline}</dd>
                                <dt>Hoort bij</dt>
                                <dd className="mono">{queue.poisonOf ?? '–'}</dd>
                                <dt>Berichten</dt>
                                <dd className="mono">{queue.depth < 0 ? 'niet te lezen' : number(queue.depth)}</dd>
                                <dt>Oudste</dt>
                                <dd>{queue.oldestInsertedOn ? `${ago(queue.oldestInsertedOn)} geleden` : '–'}</dd>
                            </dl>
                            {queue.error && <div className="notice" style={{ marginTop: 10 }}>{queue.error}</div>}
                        </div>

                        <div className="dsec" style={{ padding: 0 }}>
                            {queue.depth === 0 && <Empty>Leeg.</Empty>}
                            {queue.head.map((m) => (
                                <React.Fragment key={m.messageId}>
                                    <button className={`li ${open === m.messageId ? 'on' : ''}`} style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }} onClick={() => setOpen(open === m.messageId ? undefined : m.messageId)}>
                                        <span>
                                            <span className="t mono" style={{ fontWeight: 500 }}>{m.relationId ?? m.messageId}</span>
                                            <span className="m">
                                                <span>{m.objectType ? objectName(m.objectType) : 'Onbekend bericht'}</span>
                                                {m.eventType && <span className="mono">{m.eventType}</span>}
                                            </span>
                                        </span>
                                        <span className="mut" style={{ fontSize: 12 }}>{m.insertedOn ? `${ago(m.insertedOn)} geleden` : ''}</span>
                                    </button>
                                    {open === m.messageId && <Status customer={customer} environment={environment} message={m} queue={queue} />}
                                </React.Fragment>
                            ))}
                            {queue.depth > queue.head.length && queue.head.length > 0 && (
                                <Empty>De oudste {queue.head.length} van {number(queue.depth)}.</Empty>
                            )}
                        </div>
                    </>
                )}
            </aside>
        </>
    );
};
